import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X } from "lucide-react";
import { logo } from "@/lib/site";
import { useLang } from "@/lib/i18n";

export default function InstallPrompt() {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("ln_install_dismissed");
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!dismissed) setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem("ln_install_dismissed", "1");
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          data-testid="install-banner"
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[70] sm:max-w-sm rounded-2xl border border-border bg-card/95 backdrop-blur-xl p-4 shadow-2xl"
          style={{ boxShadow: "0 0 60px -20px hsl(174 82% 44% / 0.5)" }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-none">
              <img src={logo} alt="LocalNova" className="h-12 w-auto drop-shadow-[0_0_18px_hsl(180_80%_45%/0.4)]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-foreground">{t.install.title}</p>
              <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{t.install.desc}</p>
            </div>
            <button onClick={dismiss} data-testid="install-dismiss" aria-label={t.install.later} className="text-muted-foreground hover:text-foreground transition-colors flex-none">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={install}
              data-testid="install-accept"
              className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-accent transition-colors duration-300"
            >
              <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              {t.install.button}
            </button>
            <button
              onClick={dismiss}
              className="rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t.install.later}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
